import { useCashbackContext } from "@bokio/contexts/CashbackContext/CashbackContext";
import Icon from "@bokio/designsystem/components/Icon/Icon";
import { Heading, Paragraph, Section } from "@bokio/designsystem/components/TypographicElements/TypographicElements";
import Markdown from "@bokio/elements/Markdown/Markdown";
import { OnboardingLangFactory } from "@bokio/lang";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { formatMessage, formatNumberCurrency } from "@bokio/shared/utils/format";
import { mergeClassNames } from "@bokio/utils/classes";
import { formatPriceplanPercentage } from "@bokio/utils/priceplanUtils";

import { trialAndFreeChecklist } from "../Checklist/TrialAndFreeChecklist";
import { PromotionModalButtons } from "../PromotionModalButtons/PromotionModalButtons";
import { PromotionModalChecklist } from "../PromotionModalChecklist";

import type { PromotionListItemProps, PromotionModalInnerProps } from "../promotionModalModels";
import type { FC } from "react";

import * as styles from "../promotionModal.scss";

import BokioPlan = m.Entities.BokioPlan;
import SubscriptionInterval = m.Bokio.Common.Billing.Model.SubscriptionInterval;

const PromotionListItem: FC<PromotionListItemProps> = props => {
	const onboardingLang = OnboardingLangFactory();

	const containerClass = props.recommended
		? mergeClassNames([styles.promotionListItem, styles.recommended])
		: styles.promotionListItem;

	const checklist = trialAndFreeChecklist(props);

	const formattedPlusInfo = () => {
		const translation = onboardingLang.Onboarding_FreeToPaidPlan_PromotionItem_tagline_plus;
		const formattedCapitalNumber = formatPriceplanPercentage(
			props.cashbackContract?.DepositedCapitalCashbackPercentage || 0,
		);
		const formattedCardNumber = formatPriceplanPercentage(props.cashbackContract?.CardSpendCashbackPercentage || 0);

		return formatMessage(translation, formattedCapitalNumber, formattedCardNumber);
	};

	return (
		<div className={containerClass}>
			{props.recommended && (
				<span className={styles.recommendedBadge}>{onboardingLang.Onboarding_PromotionListItem_Recommended}</span>
			)}
			<Section resetHeadingLevelTo={3}>
				<Heading>Premium {props.plus && <span className={styles.premiumPlus}>Plus</span>}</Heading>
				{props.bba && (
					<Paragraph>
						{onboardingLang.Onboarding_FreeToPaidPlan_PromotionListItem_with_bba}
						<Icon color="blue" name="bba-filled" />
					</Paragraph>
				)}
			</Section>
			<Section>
				<PromotionModalChecklist list={checklist} />

				{!props.bba && !props.plus && (
					<Paragraph>{onboardingLang.Onboarding_FreeToPaidPlan_PromotionItem_tagline_premium}</Paragraph>
				)}

				{props.bba && !props.plus && (
					<Paragraph>{onboardingLang.Onboarding_FreeToPaidPlan_PromotionItem_tagline_bba}</Paragraph>
				)}

				{props.bba && props.plus && <Paragraph>{formattedPlusInfo()}</Paragraph>}
			</Section>
		</div>
	);
};

export const FreeToPaidPlan: FC<PromotionModalInnerProps> = ({ resolve, dismiss }) => {
	const onboardingLang = OnboardingLangFactory();

	const cashbacks = useCashbackContext();
	if (!cashbacks.initialized) {
		return null;
	}
	const recommendPlus = cashbacks.wouldBenefitFromPlus(BokioPlan.Free, SubscriptionInterval.Month, "Both");

	const plus = () => <span className={styles.highlightPlus}>Plus</span>;

	const formattedHeading = () => {
		const translation = onboardingLang.Onboarding_FreeToPaidPlan_PromotionModalHeading_cashback;
		const formattedNumber = formatNumberCurrency(cashbacks.estimatedCashback, "SEK", 0);

		return formatMessage(translation, plus(), formattedNumber);
	};

	const formattedTagline = () => {
		const translation = onboardingLang.Onboarding_FreeToPaidPlan_PromotionModalPreamble_cashback;
		const formattedNumber = formatNumberCurrency(cashbacks.estimatedCashback, "SEK", 0);

		return formatMessage(translation, formattedNumber);
	};

	return (
		<div className={styles.promotionModal} data-testid="PromotionModal_FreeToPaidPlan">
			<Section resetHeadingLevelTo={2}>
				<Heading>
					{recommendPlus
						? formattedHeading()
						: onboardingLang.Onboarding_FreeToPaidPlan_PromotionModalHeading_noCashback}
				</Heading>
				<Markdown
					markdownContent={
						recommendPlus ? formattedTagline() : onboardingLang.Onboarding_FreeToPaidPlan_PromotionModalPreamble
					}
					deprecatedOptoutFromTypographicElements
				/>
			</Section>

			<ol className={styles.promotionItemList}>
				<li>
					<PromotionListItem />
				</li>
				<li>
					<PromotionListItem bba={true} recommended={!recommendPlus} />
				</li>
				<li>
					<PromotionListItem
						bba={true}
						plus={true}
						recommended={recommendPlus}
						cashbackContract={cashbacks.defaultContract}
					/>
				</li>
			</ol>

			<PromotionModalButtons
				ctaButtonText={onboardingLang.Onboarding_FreeToPaidPlan_PromotionModal_CTA_button}
				resolve={resolve}
				dismiss={dismiss}
			/>
		</div>
	);
};
